import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronLeft, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useColorPalette } from '../hooks/useColorPalette';

/**
 * Componente Lightbox per visualizzare immagini con possibilità di navigazione
 * tra più immagini. Utilizza React Portal per montare il componente direttamente nel body
 * del documento, evitando problemi di z-index e stacking context.
 */
// Crea un elemento dedicato per il lightbox al di fuori della gerarchia dei componenti React
const createLightboxRoot = () => {
  const existingRoot = document.getElementById('lightbox-root');
  if (existingRoot) {
    // Se esiste già, assicuriamoci che sia configurato correttamente
    existingRoot.style.pointerEvents = 'auto';
    existingRoot.style.display = 'block';
    return existingRoot;
  }
  
  const lightboxRoot = document.createElement('div');
  lightboxRoot.id = 'lightbox-root';
  
  // Applica stili che garantiscono che sia sempre visibile sopra ogni altro elemento
  lightboxRoot.style.position = 'fixed';
  lightboxRoot.style.top = '0';
  lightboxRoot.style.left = '0';
  lightboxRoot.style.width = '100vw';
  lightboxRoot.style.height = '100vh';
  lightboxRoot.style.pointerEvents = 'auto';
  lightboxRoot.style.zIndex = '1000000'; // Z-index estremamente alto, verificato funzionante con l'inspector
  
  document.body.appendChild(lightboxRoot);
  return lightboxRoot;
};

const ImageLightbox = ({ isOpen, images, currentIndex = 0, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [loading, setLoading] = useState(true);
  const [lightboxRoot, setLightboxRoot] = useState(null);
  const { colors = { primary: '#007bff', secondary: '#6c757d', textOnPrimary: '#ffffff' } } = useColorPalette() || {};

  // Creazione e gestione del container dedicato per il lightbox
  useLayoutEffect(() => {
    if (isOpen) {
      const root = createLightboxRoot();
      setLightboxRoot(root);
    } else if (lightboxRoot) {
      // Quando il lightbox viene chiuso, disabilitiamo l'intercettazione degli eventi
      // ma manteniamo il container per riutilizzarlo
      lightboxRoot.style.pointerEvents = 'none';
      lightboxRoot.style.display = 'none';
    }
    return () => {
      // Pulizia alla distruzione del componente
      const root = document.getElementById('lightbox-root');
      if (root) {
        root.style.pointerEvents = 'none';
        root.style.display = 'none';
      }
    };
  }, [isOpen, lightboxRoot]);

  // Reset loading quando cambia immagine
  useEffect(() => {
    if (isOpen) setLoading(true);
  }, [activeIndex, isOpen]);

  // Reset index quando si apre il lightbox
  useEffect(() => {
    if (isOpen) {
      setActiveIndex(currentIndex);
    }
  }, [isOpen, currentIndex]);

  // Gestione dello scroll del body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handlers
  const handlePrevious = useCallback((e) => {
    e.stopPropagation();
    setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e.stopPropagation();
    setActiveIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
  }, [images.length]);

  const handleImageLoad = useCallback(() => {
    setLoading(false);
  }, []);

  // Rimosso questo controllo qui perché lo faremo più avanti

  const currentImage = images[activeIndex];
  
  // Contenuto del lightbox
  const lightboxContent = (
    <div 
      className="lightbox-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100000
      }}
    >
      <div 
        className="lightbox-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '100vw',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {/* Pulsante di chiusura */}
        <button 
          className="position-absolute bg-transparent border-0 text-white" 
          style={{ top: '15px', right: '15px', fontSize: '24px', zIndex: 1100 }}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        {/* Indicatore di caricamento */}
        {loading && (
          <div 
            className="position-absolute d-flex justify-content-center align-items-center" 
            style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 900 }}
          >
            <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: colors.primary }} />
          </div>
        )}
        
        {/* Immagine corrente */}
        <div 
          className="d-flex justify-content-center align-items-center"
          style={{ 
            height: 'calc(100vh - 120px)',
            width: '100%',
            overflow: 'hidden'
          }}
        >
          <img
            src={currentImage}
            alt={`Immagine ${activeIndex + 1}`}
            className={`mw-100 mh-100 ${loading ? 'opacity-0' : 'opacity-100'}`}
            style={{
              maxHeight: 'calc(100vh - 150px)',
              objectFit: 'contain',
              transition: 'opacity 0.3s ease-in-out'
            }}
            onLoad={handleImageLoad}
          />
        </div>
        
        {/* Contatore immagini */}
        <div className="text-white mt-2 mb-4">
          {activeIndex + 1} / {images.length}
        </div>
        
        {/* Pulsante Precedente */}
        {images.length > 1 && (
          <button 
            className="position-absolute bg-transparent border-0 text-white" 
            style={{ 
              left: '15px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              fontSize: '24px',
              zIndex: 1000
            }}
            onClick={handlePrevious}
          >
            <FontAwesomeIcon icon={faChevronLeft} size="2x" />
          </button>
        )}
        
        {/* Pulsante Successivo */}
        {images.length > 1 && (
          <button 
            className="position-absolute bg-transparent border-0 text-white" 
            style={{ 
              right: '15px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              fontSize: '24px',
              zIndex: 1000
            }}
            onClick={handleNext}
          >
            <FontAwesomeIcon icon={faChevronRight} size="2x" />
          </button>
        )}
      </div>
    </div>
  );
  
  // Se non è aperto o non ci sono immagini, non renderizzare nulla
  if (!isOpen || !images || images.length === 0 || !lightboxRoot) {
    return null;
  }
  
  // Crea un portale React per rendere il lightbox nel container dedicato con z-index molto alto
  return createPortal(lightboxContent, lightboxRoot);
};

ImageLightbox.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired
};

export default ImageLightbox;
